import { useEffect, useRef, useState } from "react";
import { Paperclip, Send, CheckCheck, Check, Zap, RotateCcw, UserCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, fmtTime, errText } from "../lib/api";
import { useSocket } from "./Layout";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { fmtDate } from "../lib/api";
import { ImageLightbox, VideoThumb } from "./ImageLightbox";

const Attachment = ({ att, kind }) => {
  const url = fileUrl(att.url);
  const [open, setOpen] = useState(false);
  if (kind === "image") return (
    <>
      <img src={url} alt={att.name} data-testid="chat-image-attachment" onClick={() => setOpen(true)} className="max-w-[240px] max-h-[200px] rounded-lg object-cover cursor-zoom-in" />
      {open && <ImageLightbox src={url} alt={att.name} onClose={() => setOpen(false)} />}
    </>
  );
  if (kind === "video") return (
    <>
      <VideoThumb src={url} testId="chat-video-attachment" className="w-[240px] h-[160px]" onOpen={() => setOpen(true)} />
      {open && <ImageLightbox src={url} alt={att.name} type="video" onClose={() => setOpen(false)} />}
    </>
  );
  return <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 underline text-sm font-semibold"><Paperclip size={14} />{att.name}</a>;
};

export const MessageBubble = ({ m, mine, theme = "agent" }) => {
  const cls = theme === "agent"
    ? m.sender === "system" ? "msg-sys" : mine ? "msg-out" : "msg-in"
    : m.sender === "system" ? "cx-msg-sys" : mine ? "cx-msg-out" : "cx-msg-in";
  return (
    <div className={`flex ${m.sender === "system" ? "justify-center" : mine ? "justify-end" : "justify-start"} fade-up`}>
      <div className={`${cls} px-3 py-2 max-w-[75%] text-sm`} data-testid={`message-${m.id}`}>
        {m.sender !== "system" && !mine && <div className="text-[10px] font-bold opacity-70 mb-0.5">{m.sender_name}</div>}
        {m.attachment && <div className="mb-1"><Attachment att={m.attachment} kind={m.type} /></div>}
        {m.order_card && (
          <div data-testid="order-card-inline" className="mt-1 mb-1 rounded-lg border-2 border-[#131E3A] bg-white p-2 text-xs min-w-[200px]">
            <div className="flex justify-between font-bold gap-3 font-heading"><span>Order #{m.order_card.number}</span><span className="uppercase text-[10px] bg-[#D4F0F0] border border-[#131E3A] rounded px-1">{m.order_card.status}</span></div>
            <div className="text-slate-500">{fmtDate(m.order_card.date)} · ₹{m.order_card.total}</div>
            {m.order_card.tracking && <div className="font-bold">🚚 {m.order_card.tracking}</div>}
            {(m.order_card.items || []).slice(0, 3).map((i, idx) => <div key={idx} className="truncate">• {i.name} ×{i.qty}</div>)}
          </div>
        )}
        {m.text && <span className="whitespace-pre-wrap break-words">{m.text}</span>}
        <div className={`flex items-center gap-1 justify-end mt-0.5 text-[10px] ${theme === "customer" && mine ? "text-blue-100" : "opacity-60"}`}>
          {fmtTime(m.created_at)}
          {mine && m.sender !== "system" && (m.status === "seen" ? <CheckCheck size={13} className={theme === "customer" ? "text-white" : "text-blue-600"} /> : m.status === "delivered" ? <CheckCheck size={13} /> : <Check size={13} />)}
        </div>
      </div>
    </div>
  );
};

export default function ChatThread({ detail, reload }) {
  const { conversation: conv, messages } = detail;
  const { user } = useAuth();
  const { sendWs, subscribe } = useSocket();
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [qrs, setQrs] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrCreateOpen, setQrCreateOpen] = useState(false);
  const [qrNew, setQrNew] = useState({ shortcut: "", text: "" });
  const [agents, setAgents] = useState([]);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [remarks, setRemarks] = useState([]);
  const [qaOpen, setQaOpen] = useState(false);
  const [qaRating, setQaRating] = useState(0);
  const [qaRemarks, setQaRemarks] = useState("");
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const typingTimer = useRef(null);

  useEffect(() => { api.get("/quick-replies").then((r) => setQrs(r.data)); api.get("/agents").then((r) => setAgents(r.data)); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  useEffect(() => subscribe((d) => {
    if (d.event === "typing" && d.conversation_id === conv.id && d.from === "customer") {
      setTyping(true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setTyping(false), 2500);
    }
    if (d.event === "message" && d.conversation_id === conv.id) setTyping(false);
  }), [subscribe, conv.id]);

  const send = async (payload) => {
    try {
      await api.post(`/conversations/${conv.id}/messages`, payload);
    } catch (e) { toast.error(errText(e)); }
  };

  const submit = (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    send({ type: "text", text: text.trim() });
    setText(""); setQrOpen(false);
  };

  const onType = (v) => {
    setText(v);
    setQrOpen(v.startsWith("/"));
    sendWs({ event: "typing", conversation_id: conv.id });
  };

  const pickQr = (qr) => {
    setQrOpen(false); setText("");
    send({ type: qr.attachment ? qr.attachment.kind || "file" : "text", text: qr.text, attachment: qr.attachment });
  };

  const uploadFile = async (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("File is too large — max 25 MB"); return; }
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd);
      await send({ type: data.kind, text: "", attachment: { url: data.url, name: data.name, content_type: data.content_type, size: data.size } });
    } catch (e) { toast.error(errText(e)); }
  };

  const doResolve = async (remark) => {
    await api.post(`/conversations/${conv.id}/resolve`, { remark });
    setResolveOpen(false); reload();
    toast.success("Ticket closed");
  };

  const saveMiniQr = async () => {
    if (!qrNew.shortcut || !qrNew.text) { toast.error("Shortcut and text are required"); return; }
    try {
      await api.post("/quick-replies", qrNew);
      setQrNew({ shortcut: "", text: "" });
      setQrCreateOpen(false);
      const { data } = await api.get("/quick-replies");
      setQrs(data);
      toast.success("Quick reply saved");
    } catch (e) { toast.error(errText(e)); }
  };

  const saveQa = async () => {
    if (!qaRating) { toast.error("Pick a rating first"); return; }
    try {
      await api.post(`/conversations/${conv.id}/qa`, { rating: qaRating, remarks: qaRemarks });
      setQaOpen(false); reload();
      toast.success("QA rating saved");
    } catch (e) { toast.error(errText(e)); }
  };

  const convAgeH = (Date.now() - new Date(conv.created_at).getTime()) / 3600000;
  const slaBreached = conv.status === "open" && convAgeH > 48;
  const slaRisk = conv.status === "open" && convAgeH > 36 && convAgeH <= 48;

  const filteredQrs = qrs.filter((q) => q.shortcut.toLowerCase().includes(text.slice(1).toLowerCase()));

  return (
    <>
      <div className="flex items-center justify-between px-4 py-2 border-b-2 border-[#131E3A] gap-2">
        <div className="min-w-0">
          <div className="font-heading font-bold truncate flex items-center gap-2">
            <span className="truncate">{conv.customer_name} <span className="text-xs font-normal text-slate-500">· {conv.ticket_number}</span></span>
            {slaBreached && <span data-testid="sla-breached-chip" className="text-[10px] font-bold uppercase bg-red-100 text-red-700 border border-red-600 rounded px-1.5 py-0.5 shrink-0">⚠ SLA breached</span>}
            {slaRisk && <span data-testid="sla-risk-chip" title={`Open for ${Math.floor(convAgeH)}h — breaches at 48h`} className="text-[10px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-600 rounded px-1.5 py-0.5 shrink-0">⏳ SLA {Math.max(0, Math.ceil(48 - convAgeH))}h left</span>}
            {conv.source === "snd.in link" && <span data-testid="source-link-chip" title="Started from link (details not verified) — name/phone were supplied by the link, not confirmed by the customer" className="text-[10px] font-bold uppercase bg-sky-100 text-sky-700 border border-sky-600 rounded px-1.5 py-0.5 shrink-0">🔗 via snd.in link</span>}
            {conv.order_number && <span data-testid="conv-order-chip" title="Order number supplied with this chat" className="text-[10px] font-bold uppercase bg-[#FDF0A0] border border-[#131E3A] rounded px-1.5 py-0.5 shrink-0">Order #{conv.order_number}</span>}
            {conv.qa_rating && <span data-testid="qa-rating-chip" title={conv.qa_remarks ? `${conv.qa_remarks} — ${conv.qa_by}` : `Rated by ${conv.qa_by}`} className="text-[10px] font-bold uppercase bg-[#E8DFF5] border border-[#131E3A] rounded px-1.5 py-0.5 shrink-0">QA {"★".repeat(conv.qa_rating)}</span>}
          </div>
          <div className="text-[11px] text-slate-500">{conv.status === "resolved" ? `Closed: ${conv.closed_remark}${conv.csat_rating ? ` · CSAT ${"★".repeat(conv.csat_rating)}` : ""}` : conv.assigned_name ? `Assigned to ${conv.assigned_name}` : "Unassigned"}</div>
        </div>
        <div className="flex gap-2 items-center shrink-0">
          {user?.role === "admin" && (
            <button data-testid="qa-rate-button" className="btn-outline-tactile px-2.5 py-1.5 text-xs" title="Internal quality rating"
              onClick={() => { setQaRating(conv.qa_rating || 0); setQaRemarks(conv.qa_remarks || ""); setQaOpen(true); }}>
              QA {conv.qa_rating ? "★".repeat(conv.qa_rating) : "rate"}
            </button>
          )}
          {user?.role === "admin" && (
            <select data-testid="assign-select" className="sd-input py-1 text-xs w-32" value={conv.assigned_to || ""}
              onChange={async (e) => { await api.post(`/conversations/${conv.id}/assign`, { agent_id: e.target.value || null }); reload(); }}>
              <option value="">Unassigned</option>
              {agents.filter((a) => a.is_active).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          {conv.status === "open" ? (
            <button data-testid="resolve-button" className="btn-tactile px-3 py-1.5 text-xs flex items-center gap-1" onClick={async () => { const { data } = await api.get("/settings"); setRemarks(data.preset_remarks || []); setResolveOpen(true); }}>
              <UserCheck size={13} /> Resolve
            </button>
          ) : (
            <button data-testid="reopen-button" className="btn-outline-tactile px-3 py-1.5 text-xs flex items-center gap-1" onClick={async () => { await api.post(`/conversations/${conv.id}/reopen`); reload(); }}>
              <RotateCcw size={13} /> Reopen
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-thread">
        {messages.map((m) => <MessageBubble key={m.id} m={m} mine={m.sender === "agent"} />)}
        {typing && <div className="flex items-center gap-1.5 text-xs text-slate-500 pl-1"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /><span className="ml-1">customer is typing…</span></div>}
        <div ref={bottomRef} />
      </div>
      <div className="relative border-t-2 border-[#131E3A] p-3">
        {qrOpen && <div className="fixed inset-0 z-[5]" onClick={() => { setQrOpen(false); if (text === "/") setText(""); }} />}
        {qrOpen && (
          <div className="absolute bottom-full left-3 right-3 mb-1 card-tactile max-h-60 overflow-y-auto z-10" data-testid="quick-reply-popup">
            <div className="flex items-center justify-between px-3 py-1.5 border-b-2 border-[#131E3A] bg-[#FDF0A0] sticky top-0">
              <span className="text-[10px] font-bold uppercase tracking-wider font-heading">Quick replies</span>
              <div className="flex items-center gap-2">
                <button type="button" data-testid="qr-new-button" className="text-[10px] font-bold uppercase tracking-wider underline" onClick={() => setQrCreateOpen(!qrCreateOpen)}>+ New</button>
                <button type="button" data-testid="quick-reply-close" onClick={() => { setQrOpen(false); setQrCreateOpen(false); if (text === "/") setText(""); }}><X size={14} /></button>
              </div>
            </div>
            {qrCreateOpen && (
              <div className="p-2 border-b border-dashed border-slate-300 space-y-1.5 bg-white">
                <input data-testid="qr-mini-shortcut" className="sd-input py-1 text-xs" placeholder="shortcut (no spaces)" value={qrNew.shortcut} onChange={(e) => setQrNew({ ...qrNew, shortcut: e.target.value.replace(/\s/g, "") })} />
                <textarea data-testid="qr-mini-text" className="sd-input py-1 text-xs resize-none" rows={2} placeholder="Reply text…" value={qrNew.text} onChange={(e) => setQrNew({ ...qrNew, text: e.target.value })} />
                <button type="button" data-testid="qr-mini-save" className="btn-tactile px-3 py-1 text-xs" onClick={saveMiniQr}>Save my quick reply</button>
              </div>
            )}
            {filteredQrs.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No matching quick replies</div>}
            {filteredQrs.map((q) => (
              <button key={q.id} type="button" data-testid={`quick-reply-${q.shortcut}`} onClick={() => pickQr(q)} className="w-full text-left px-3 py-2 hover:bg-[#FDF0A0] border-b border-dashed border-slate-300 last:border-0 flex items-center gap-2">
                {q.attachment && (q.attachment.kind === "image" ? <img src={fileUrl(q.attachment.url)} alt="" className="w-9 h-9 rounded object-cover border border-[#131E3A] shrink-0" /> : <Paperclip size={12} className="shrink-0" />)}
                <div className="min-w-0 flex-1">
                  <span className="font-bold text-xs font-heading">/{q.shortcut}</span>
                  {q.scope === "personal" && <span className="ml-1.5 text-[9px] font-bold uppercase bg-[#E8DFF5] border border-[#131E3A] rounded px-1">mine</span>}
                  <div className="text-xs text-slate-600 truncate">{q.text || q.attachment?.name}</div>
                </div>
              </button>
            ))}
          </div>
        )}
        <form onSubmit={submit} className="relative z-20 flex gap-2 items-center">
          <button type="button" data-testid="attach-button" className="btn-outline-tactile p-2" onClick={() => fileRef.current?.click()}><Paperclip size={15} /></button>
          <input ref={fileRef} type="file" hidden onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} />
          <button type="button" data-testid="qr-toggle-button" className="btn-outline-tactile p-2" title='Quick replies (type "/")' onClick={() => { if (qrOpen) { setQrOpen(false); if (text === "/") setText(""); } else { setText("/"); setQrOpen(true); } }}><Zap size={15} /></button>
          <textarea data-testid="composer-input" className="sd-input flex-1 resize-none" rows={Math.min(4, (text.match(/\n/g) || []).length + 1)}
            placeholder='Type a reply… Enter to send, Shift+Enter for new line, "/" for quick replies' value={text} onChange={(e) => onType(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { setQrOpen(false); if (text === "/") setText(""); }
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
            }} />
          <button data-testid="send-button" className="btn-tactile p-2.5"><Send size={15} /></button>
        </form>
      </div>
      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent className="card-sticky border-2 border-[#131E3A]">
          <DialogHeader><DialogTitle className="font-heading">Close ticket {conv.ticket_number}</DialogTitle></DialogHeader>
          <p className="sd-label">Select a closing remark</p>
          <div className="space-y-2">
            {remarks.map((r) => (
              <button key={r} data-testid={`remark-${r}`} onClick={() => doResolve(r)} className="btn-outline-tactile w-full py-2 text-sm text-left px-3">{r}</button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={qaOpen} onOpenChange={setQaOpen}>
        <DialogContent className="card-sticky border-2 border-[#131E3A]">
          <DialogHeader><DialogTitle className="font-heading">Internal QA rating · {conv.ticket_number}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-600">Visible to the team only — the customer never sees this.</p>
          <div className="flex gap-1.5 justify-center py-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`qa-star-${n}`} onClick={() => setQaRating(n)}
                className={`text-3xl leading-none transition-transform hover:scale-110 ${n <= qaRating ? "text-amber-500" : "text-slate-300"}`}>★</button>
            ))}
          </div>
          <textarea data-testid="qa-remarks-input" className="sd-input text-sm resize-none" rows={3} placeholder="Remarks (optional) — e.g. great tone, missed greeting…" value={qaRemarks} onChange={(e) => setQaRemarks(e.target.value)} />
          <button data-testid="qa-save-button" className="btn-tactile px-4 py-2 text-sm" onClick={saveQa}>Save QA rating</button>
        </DialogContent>
      </Dialog>
    </>
  );
}
